import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { plainToClass } from 'class-transformer';

import { ConstantsService } from 'app/services/constants-service';
import { ObservableRequestService } from 'app/services/observable-request.service';

import { BankAccount } from 'app/shared/models/bank-account.model';
import { BankInstitution } from 'app/shared/models/bank-institution.model';
import {CookieService} from '../services/cookie.service';
import {Headers} from '@angular/http';
import {environment} from '../../environments/environment';

/**
* Service for the Finance components.
*/
@Injectable()
export class FinanceService {
    constructor(private requestService: ObservableRequestService,  private cookieService: CookieService) { }

    getBankInstitutionsList(): Observable<any> {
        return this.requestService.getRequest(ConstantsService.BANK_INSTITUTIONS_ENDPOINT)
            .map(response => {
                return plainToClass(BankInstitution, response.arrResult as Object);
            });
    }

    getBankInstitutionsAccountsForEntity(entityId: number): Observable<any> {
        return this.requestService.getRequest(`${ConstantsService.BANK_INSTITUTIONS_ACCOUNTS_ENDPOINT}/${entityId}`)
            .map(response => {
                return plainToClass(BankInstitution, response.arrResult as Object);
            });
    }

    getBankStatementsDetails(bankAccountId: number, page: number = 0, size: number = 500): Observable<any> {
      // Note: this endpoint is used inside the Client file → Bank account → Bank statements table.
      // That table paginates client-side; so we fetch a larger page from the backend to ensure
      // older statements (including "confirmed but not pushed") are visible.
      return this.requestService.getRequest(
        `${ConstantsService.BANK_STATEMENT_DETAILS_ENDPOINT}/${bankAccountId}?page=${page}&size=${size}`
      );
    }

    getBankAccountsForEntity(entityId: number): Observable<any> {
        const clientAccountsURL = `${ConstantsService.BANK_ACCOUNTS_ENDPOINT}/${entityId}`;
        return this.requestService.getRequest(clientAccountsURL)
            .map(response => {
                if (response.arrResult) {
                    return plainToClass(BankAccount, response.arrResult as Object);
                } else {
                    return response;
                }
            });
    };

    saveBankInstitution(body: Object): Observable<any> {
        return this.requestService
            .postRequest(ConstantsService.BANK_INSTITUTIONS_ENDPOINT,
            JSON.stringify(body),
            this.requestService.getJSONContentType());
    };

    deleteBankInstitution(bankInstitutionId: number): Observable<any> {
        return this.requestService.deleteRequest(ConstantsService.BANK_INSTITUTIONS_ENDPOINT,
            this.requestService.serialize({bankInstitutionId : bankInstitutionId}),
            this.requestService.getFormEncodedContentType());
    }

    saveBankAccount(body: Object): Observable<any> {
        // Use POST for creating new bank accounts (when accountId is null/undefined)
        return this.requestService
            .postRequest(ConstantsService.BANK_ACCOUNTS_ENDPOINT,
            JSON.stringify(body),
            this.requestService.getJSONContentType());
    };

    updateBankAccount(accountId: number, body: Object): Observable<any> {
        // Use PUT for updating existing bank accounts
        const url = `${ConstantsService.BANK_ACCOUNTS_ENDPOINT}/${accountId}`;
        return this.requestService
            .putRequest(url,
            JSON.stringify(body),
            this.requestService.getJSONContentType());
    };

    deleteBankAccount(bankAccountId: number, entityId?: number): Observable<any> {
        const body: { bankAccountId: number, entityId?: number } = { bankAccountId: bankAccountId };
        if (entityId != null) {
            body.entityId = entityId;
        }
        return this.requestService.deleteRequest(ConstantsService.BANK_ACCOUNTS_ENDPOINT,
            this.requestService.serialize(body),
            this.requestService.getFormEncodedContentType());
    }

    initiateSyncCitco(params: Object): Observable<any> {
        return this.requestService
            .postRequest(ConstantsService.SYNC_CITCO,
            this.requestService.serialize(params),
            this.requestService.getFormEncodedContentType()
        );
    }

    saveBankStatementDocument(file: File, bankAccountId: number): Observable<any> {
      const formData: FormData = new FormData();
      formData.append('file', file);
      formData.append('bankAccountId', bankAccountId);
      //setting headers
      const headers: Headers = new Headers();
      //post request
      return this.requestService.postRequest(ConstantsService.SAVE_BANK_STATEMENT_DOCUMENT, formData, headers);
    }

    saveBankStatementJsonObject(json: any, bankStatementId: number): Observable<any> {
      return this.requestService
        .postRequest(`${ConstantsService.SAVE_BANK_STATEMENT_JSON_DATA}/${bankStatementId}`, json, new Headers());
    }

    getDocumentAnalysis(uniqueDocumentId: string, bkId: number, tenantId: number, bankStatementId?: number, documentPath?: string | null): Observable<any> {
      const url = `/bank-statement/analyze-document`;
      const payload: any = {};
      
      // If documentPath is explicitly provided (including null), use it
      // Otherwise, construct the path (for backward compatibility)
      if (documentPath !== undefined) {
        payload.documentPath = documentPath;
      } else {
        // Construct path only if bankStatementId is not provided (legacy behavior)
        if (!bankStatementId) {
          payload.documentPath = `/opt/trust/documents/TENANT-${tenantId}/BANK-ACC-${bkId}/${uniqueDocumentId}`;
        }
        // If bankStatementId is provided, let backend construct the path
      }
      
      if (bankStatementId) {
        payload.bankStatementId = bankStatementId;
      }
      
      return this.requestService.postRequest(url, payload, new Headers());
    }
    
    getProcessingStatus(bankStatementId: number): Observable<any> {
      const url = `/bank-statement/${bankStatementId}/processing-status`;
      return this.requestService.getRequest(url);
    }

    deleteBankStatement(id: string): Observable<any> {
      const url = `/bank-statement/${id}`;
      return this.requestService.deleteRequest(url, {}, new Headers());
    }

    downloadBankStatement(uniqueDocumentName: string, bankAccountId: number, tenantId: number, documentName: string): Observable<Blob> {
      const url = `/bank-statement/download?uniqueDocumentName=${encodeURIComponent(uniqueDocumentName)}&bankAccountId=${bankAccountId}&tenantId=${tenantId}&documentName=${encodeURIComponent(documentName)}`;
      return this.requestService.downloadFile(url);
    }

    pushToTransactionMonitoring(bankStatementId: number, clientId?: number): Observable<any> {
      let url = `/bank-statement/${bankStatementId}/push-to-transaction-monitoring`;
      if (clientId != null) {
        url += `?clientId=${clientId}`;
      }
      return this.requestService.postRequest(url, {}, new Headers());
    }

    moveBankStatement(bankStatementId: number, clientId: number, bankAccountId: number): Observable<any> {
      const url = `/bank-statement/${bankStatementId}/move`;
      return this.requestService.postRequest(url, { clientId, bankAccountId }, new Headers());
    }

    applyReanalyzeMerge(bankStatementId: number, mergeResult: any, newJsonData: any): Observable<any> {
      const url = `/bank-statement/${bankStatementId}/apply-reanalyze-merge`;
      return this.requestService.postRequest(url, { mergeResult, newJsonData }, new Headers());
    }

    /**
     * Updates bank statement period dates (statement_start_date / statement_end_date).
     * Provide either startDateMs and/or endDateMs.
     */
    updateBankStatementPeriod(bankStatementId: number, startDateMs?: number | null, endDateMs?: number | null): Observable<any> {
      const url = `/bank-statement/${bankStatementId}/update-period`;
      const payload: any = {};
      if (startDateMs != null) {
        payload.statementStartDate = startDateMs;
      }
      if (endDateMs != null) {
        payload.statementEndDate = endDateMs;
      }
      return this.requestService.postRequest(url, payload, new Headers());
    }

    checkDuplicates(bankStatementId: number): Observable<any> {
      const url = `/bank-statement/${bankStatementId}/check-duplicates`;
      return this.requestService.postRequest(url, {}, new Headers());
    }

    confirmBankStatement(bankStatementId: number): Observable<any> {
      return this.requestService.postRequest(
        ConstantsService.BANK_STATEMENT_BATCH_CONFIRM,
        { bankStatementId },
        new Headers()
      );
    }
}



// WEBPACK FOOTER //
// ./src/app/finance/finance.service.ts